import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { useVideoCredits } from './useVideoCredits';
import { useVideoCreditGate } from './useVideoCreditGate';
import { CreditBlockView, creditBlockView } from './creditBlockView';
import { creditMessages } from './creditMessages';
import { CreditSplit } from './CreditSplit';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the credit block; each part uses its own slice of it */
import styles from './StudioCreditBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StudioStickyMakeBarProps {
  accountId: number;
  /** The credits of this make: 3 for a full video, 1 for a short video, 0 for an image. */
  cost: number;
  /** True for an asset that needs a paid plan (a full video, the motion artwork). */
  needsPlan?: boolean;
  /** False hides the "This needs 3 credits" note. */
  showShortNote?: boolean;
  /** The test id of the "This needs 3 credits" note. */
  shortNoteTestId?: string;
  /** The "Make it" button of the rail. */
  children: React.ReactNode;
}

function useCostText(cost: number, view: CreditBlockView | null): string {
  const intl = useIntl();
  if (view?.costKind === 'plan') {
    return intl.formatMessage(creditMessages.blockProPlan);
  }
  if (view?.costKind === 'free' || cost <= 0) {
    return intl.formatMessage(creditMessages.blockFree);
  }
  return intl.formatMessage(creditMessages.blockBarCost, { count: cost });
}

/**
 * The narrow form of the credit block (design release-kit t5b). Below 700 px the cost, the balance
 * and "Make it" stick to the bottom of the page as one bar. "How?" opens the split above the bar.
 * On the web the bar sits above the mobile bottom bar; in a native app's web view it sits at the
 * bottom. The bar goes into `document.body`, so no parent with a transform or a filter can move it.
 * A spacer in the rail keeps the end of the page clear of the bar.
 */
const StudioStickyMakeBarComponent: React.FC<StudioStickyMakeBarProps> = (props) => {
  const { accountId, cost, needsPlan = false, showShortNote = true, shortNoteTestId, children } = props;
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const { credits } = useVideoCredits({ accountId });
  const gate = useVideoCreditGate(accountId);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const view = credits === null ? null : creditBlockView(credits, cost, needsPlan);
  const costText = useCostText(cost, view);

  if (typeof document === 'undefined') {
    return null;
  }

  const hasSplit = credits !== null && credits.limit !== null;
  const haveText =
    view === null
      ? null
      : view.left === null
        ? intl.formatMessage(creditMessages.blockNoLimit)
        : intl.formatMessage(creditMessages.blockBarHave, { count: view.left });

  const bar = (
    <div className={clsx(styles.sticky, !isNativeEmbed && styles.stickyAboveNav)} data-testid="studio-sticky-make-bar">
      {hasSplit && isOpen && credits !== null && view !== null && <CreditSplit credits={credits} view={view} />}
      {view !== null && view.isShort && showShortNote && (
        <span className={clsx('small', styles.warn)} data-testid={shortNoteTestId}>
          {gate.shortNote(cost)}
        </span>
      )}
      <div className={styles.stickyRow}>
        <div className={styles.stickyText}>
          <b
            className={clsx(
              styles.stickyCost,
              view?.costKind === 'free' && styles.valueGood,
              view?.costKind === 'plan' && styles.valueLock,
              view?.isShort === true && styles.valueLow
            )}
          >
            {costText}
          </b>
          {haveText !== null && (
            <small className={styles.stickySub}>
              {haveText}
              {hasSplit && (
                <>
                  {' · '}
                  <button type="button" className={styles.stickyHow} aria-expanded={isOpen} onClick={toggle}>
                    {intl.formatMessage(creditMessages.blockBarHow)}
                  </button>
                </>
              )}
            </small>
          )}
        </div>
        {children}
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.stickySpacer} aria-hidden="true" />
      {createPortal(bar, document.body)}
    </>
  );
};

export const StudioStickyMakeBar = TypedMemo(StudioStickyMakeBarComponent);
